import { Directive, HostBinding, Inject, Input, OnChanges, OnDestroy, OnInit, Optional } from '@angular/core'
import { Subject, combineLatest, distinctUntilChanged, switchMap, takeUntil } from 'rxjs'
import { FeatureFlagKeyOfType } from '../feature-flags'
import { LaunchDarklyService } from '../services'
import { FEATURE_FLAG_OVERRIDES, FeatureFlagOverrides } from '../services/feature-flag-overrides'

@Directive({
  selector: '[applyClassIfFeatureFlagDisabled]',
  standalone: false,
})
export class ApplyClassIfFeatureFlagDisabledDirective implements OnInit, OnChanges, OnDestroy {
  @Input() applyClassIfFeatureFlagDisabled: FeatureFlagKeyOfType<boolean>

  @Input() featureFlaggedClassName: string | undefined

  @HostBinding('class') appliedClass: string | undefined = undefined

  readonly finalize = new Subject<void>()
  readonly flagChanged = new Subject<FeatureFlagKeyOfType<boolean>>()
  readonly classNameChanged = new Subject<string | undefined>()

  constructor(
    private ldService: LaunchDarklyService,
    @Optional() @Inject(FEATURE_FLAG_OVERRIDES) public overrides: FeatureFlagOverrides | undefined,
  ) {}

  ngOnInit(): void {
    if (this.overrides && this.applyClassIfFeatureFlagDisabled) {
      const flagEnabled = this.overrides.allFlags[this.applyClassIfFeatureFlagDisabled]
      this.updateClassName(!flagEnabled)
    } else {
      combineLatest([this.flagChanged.pipe(distinctUntilChanged()), this.classNameChanged.pipe(distinctUntilChanged())])
        .pipe(
          switchMap(([key]) => this.ldService.observeFlagChanges<boolean>(key)),
          takeUntil(this.finalize),
        )
        .subscribe(enabled => this.updateClassName(!enabled))
      this.flagChanged.next(this.applyClassIfFeatureFlagDisabled)
      this.classNameChanged.next(this.featureFlaggedClassName)
    }
  }

  ngOnChanges(): void {
    this.flagChanged.next(this.applyClassIfFeatureFlagDisabled)
    this.classNameChanged.next(this.featureFlaggedClassName)
  }

  ngOnDestroy(): void {
    this.finalize.next()
    this.finalize.complete()
  }

  private updateClassName(flagDisabled: boolean): void {
    this.appliedClass = flagDisabled ? this.featureFlaggedClassName : undefined
  }
}
